"use client";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/shadcn/dialog";
import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { joinWebinarEvent } from "../../_actions/webinar/join-webinar-event";
import type { WebinarFormData } from "./types";
import { WebinarForm } from "./webinar-form";

interface WebinarRegistrationModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    eventId: number;
    eventTitle: string;
}

export function WebinarRegistrationModal({
    open,
    onOpenChange,
    eventId,
    eventTitle,
}: WebinarRegistrationModalProps) {
    const t = useTranslations("events");

    const router = useRouter();

    const joinMutation = useMutation({
        mutationFn: (data: WebinarFormData) => joinWebinarEvent(eventId, data),
        onSuccess: (result) => {
            if (!result.success) {
                toast.error(result.error?.message || t("applicationError"));
                return;
            }

            toast.success(result.message || t("applicationSuccess"));
            onOpenChange(false);
            router.refresh();
        },
        onError: (error) => {
            console.error("Form submission error:", error);
            toast.error(t("applicationErrorRetry"));
        },
    });

    const handleSubmit = async (data: WebinarFormData) => {
        joinMutation.mutate(data);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{t("registrationTitle")}</DialogTitle>
                    <DialogDescription>
                        {t("registrationBody")}
                    </DialogDescription>
                </DialogHeader>
                <WebinarForm
                    eventTitle={eventTitle}
                    onSubmit={handleSubmit}
                    onBack={() => onOpenChange(false)}
                    isSubmitting={joinMutation.isPending}
                />
            </DialogContent>
        </Dialog>
    );
}
